import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  InstantSearch,
  RefinementList,
  Stats,
  CurrentRefinements,
  ClearRefinements,
  Configure,
  Hits,
} from "react-instantsearch";
import DictionaryHit from "../../components/Dictionary/DictionaryHits";
import DictionaryPagination from "../../components/Dictionary/DictionaryPagination";
import SearchLayout from "../../layouts/SearchLayout";
import { searchClient } from "../../services/typesense-client";
import { safeRouting } from "../../services/instantsearch-routing";
import { Helmet } from "react-helmet-async";
import abcIcon from "../../assets/images/Alphabet_color1.png";
import { FiFilter } from "react-icons/fi";
import MobileAlphabetFilter from "../../components/Header/AlphabetFilter/MobileAlphabetFilter";
import { CategoryFilterModal } from "../../components/Header/CategoryFilter";
import { useAuthContext } from "@asgardeo/auth-react";

const TrilingualDictionary = () => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const PlaceHolder = "Type a word, சொல், වචනය";
  const { state, getAccessToken } = useAuthContext();
  const [userToken, setUserToken] = useState("noToken");

  useEffect(() => {
    let cancelled = false;
    const loadToken = async () => {
      if (!state.isAuthenticated) {
        if (!cancelled) setUserToken("noToken");
        return;
      }
      const token = await getAccessToken();
      if (!cancelled) setUserToken(token || "noToken");
    };
    loadToken().catch(() => !cancelled && setUserToken("noToken"));
    return () => { cancelled = true; };
  }, [getAccessToken, state.isAuthenticated]);

  const DictionaryClient = useMemo(
    () =>
      searchClient(
        "english, tamil, sinhala",
        "off",
        userToken,
        "dictionary",
        "english:asc"
      ),
    [userToken]
  );

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const commonClassNames = {
    root: "flex flex-col justify-center",
    noRefinementRoot: "",
    noResults: "text-11 pl-3",
    item: "flex flex-col mb-1",
    selectedItem: "",
    list: "list-none w-full px-0 mb-0",
    count:
      "bg-[#9d9d9d30] rounded-full lg:text-10 font-normal lg:px-2 px-1 ml-20 dark:bg-[#434343]",
    checkbox: "ml-5 mr-2 flex-shrink-0 background-color: #df0d7d",
    label:
      "flex items-center px-10 text-12 mb-1 text-pl-text-tertiary",
    labelText: "block truncate hover:whitespace-normal",
  };

  return (
    <div className="relative">
      {/* Main Content */}
      <InstantSearch
        indexName={import.meta.env.VITE_DICTIONARY_TYPESENSE_COLLECTION_NAME}
        searchClient={DictionaryClient}
        routing={safeRouting}
        future={{
          preserveSharedStateOnUnmount: true,
        }}
      >
        <div className="w-full flex-grow">
          <Helmet>
            <title>Trilingual Dictionary | paralegal.lk</title>
          </Helmet>

          <SearchLayout
            isResults={true}
            showTab={false}
            showAlphabet={true}
            showSearch={true}
            PlaceHolder={PlaceHolder}
            enableFiltering={false}
            enableSearchTracking={false}
            enableSuggestions={false}
            SearchAsYouType={true}
          >
            <div className="flex flex-col py-20 pt-[120px] max-w-6xl w-full px-3 md:px-0">
              <div className="px-10 mb-8 md:px-20 flex flex-nowrap w-full overflow-x-auto py-1">
                <CurrentRefinements
                  classNames={{
                    root: "flex whitespace-nowrap",
                    noRefinementRoot: "",
                    noRefinementList: "",
                    item: "",
                    label: "hidden",
                    category:
                      "mx-0.5 rounded-full py-0.5 px-2 text-13 transition-all text-pl-text-primary bg-pl-bg-elevated border border-pl-border",
                    categoryLabel: "me-1",
                    list: "flex flex-nowrap",
                  }}
                />
                <ClearRefinements
                  translations={{
                    resetButtonText: "Clear all",
                  }}
                  classNames={{
                    root: "",
                    button:
                      "whitespace-nowrap mx-0.5 rounded-full py-0.5 px-2 text-13 transition-all text-pl-text-primary bg-pl-bg-subtle border border-pl-border",
                    disabledButton: "hidden",
                  }}
                />
              </div>

              <Configure hitsPerPage={15} />

              {/* Stats display */}
              <div className="flex w-full md:gap-50 text-12 md:text-14 items-center mb-2">
                <div className="flex flex-grow w-3/4 px-6 text-pl-text-tertiary">
                  <Stats
                    translations={{
                      rootElementText(nbHits) {
                        let statsText = "";
                        if (nbHits.nbHits === 0) {
                          statsText = "No results";
                        } else if (nbHits.nbHits === 1) {
                          statsText = "1 result";
                        } else {
                          statsText = `${nbHits.nbHits} results`;
                        }
                        return `${statsText.toLocaleString()} found in ${
                          nbHits.processingTimeMS
                        } ms.`;
                      },
                    }}
                  />
                </div>
                <div className="flex justify-start items-center gap-10 mr-20 md:mr-0 sm:gap-30 sm:w-1/4">
                  {!isDesktop && (
                    <div>
                      <div className="flex items-center gap-3 ml-3 relative">
                        <button
                          onClick={() => setShowCategoryModal(true)}
                          className="flex items-center justify-center hover:bg-pl-accent/10 dark:hover:bg-pl-accent/20 transition-all"
                          aria-label="Filter by Category"
                        >
                          <FiFilter className="w-[18px] h-[18px] text-pl-accent" />
                        </button>
                        {/* New Category Filter Modal */}
                        <CategoryFilterModal
                          isOpen={showCategoryModal}
                          onClose={() => setShowCategoryModal(false)}
                        />
                        <button
                          onClick={() => setShowDropdown(!showDropdown)}
                          className="relative"
                        >
                          <img
                            src={abcIcon}
                            alt="Alphabet_color"
                            className="w-[20px] h-[20px]"
                          />
                        </button>

                        <MobileAlphabetFilter
                          isOpen={showDropdown}
                          onClose={() => setShowDropdown(false)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Terms List */}
              <div className="flex w-full md:gap-[44px]">
                <div className="md:w-3/4 w-full flex flex-col justify-between h-full">
                  <div className="flex-1 text-pl-text-tertiary">
                    <Hits hitComponent={DictionaryHit} />
                  </div>
                  <div className="mt-50">
                    <DictionaryPagination />
                  </div>
                </div>
                {isDesktop && (
                  <div className="flex w-1/4 px-10">
                    <div className="pl-filter flex flex-col w-full self-start">
                      <div className="bg-pl-bg-elevated overflow-hidden border border-pl-border rounded-lg">
                        {/* Categories */}
                        <h2 className="py-6 px-10 cursor-pointer flex text-left text-14 md:text-14 font-medium text-pl-text-tertiary hover:bg-pl-bg-subtle">
                          Filter by category
                        </h2>
                        <RefinementList
                          attribute="category"
                          showMore={false}
                          limit={100}
                          sortBy={["name:asc"]}
                          classNames={commonClassNames}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </SearchLayout>
        </div>
      </InstantSearch>
    </div>
  );
};

export default TrilingualDictionary;
